import React, { useState } from 'react';
import { Pin, PinOff, CheckCircle2, ArrowUp, ArrowDown, AppWindow } from 'lucide-react';
import { useApplications, APP_ICON_MAP, APP_COLOR_MAP } from '../hooks/useApplications';

export default function PinnedAppsSettings() {
  const { applications, pinnedApplications, updatePinned } = useApplications();
  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  const handleSelectAppForPosition = async (posIndex, appSlug) => {
    const nextList = [...pinnedApplications.map((a) => a.slug)];
    if (!appSlug) {
      // Remove at posIndex
      nextList.splice(posIndex, 1);
    } else {
      // Remove existing occurrence if already present
      const filtered = nextList.filter((s) => s !== appSlug);
      // Insert at posIndex
      filtered.splice(posIndex, 0, appSlug);
      nextList.length = 0;
      nextList.push(...filtered.slice(0, 3));
    }

    setSaving(true);
    try {
      await updatePinned(nextList);
      showToast('Pinned applications updated successfully');
    } catch (err) {
      showToast('Failed to update pinned applications');
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async (index, direction) => {
    const nextList = [...pinnedApplications.map((a) => a.slug)];
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= nextList.length) return;

    const temp = nextList[index];
    nextList[index] = nextList[targetIdx];
    nextList[targetIdx] = temp;

    setSaving(true);
    try {
      await updatePinned(nextList);
      showToast('Application position updated');
    } catch (err) {
      showToast('Failed to reorder applications');
    } finally {
      setSaving(false);
    }
  };

  const availableApps = applications.filter((a) => a.enabled);

  return (
    <div className="pinned-settings-container glass-panel" style={{ padding: '24px', borderRadius: '16px' }}>
      {toastMsg && (
        <div className="admin-toast admin-toast-success animate-fade-in" style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999 }}>
          <CheckCircle2 size={16} />
          <span>{toastMsg}</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <Pin size={20} color="#0d9488" />
        <h3 style={{ fontSize: '1.15rem', fontWeight: '600' }}>Pinned Header Applications</h3>
      </div>
      <p style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', marginBottom: '20px' }}>
        Configure up to 3 applications to display in your main header bar for 1-click launch. Only authorized applications can be pinned.
      </p>

      <div className="pinned-slots-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {[0, 1, 2].map((slotIdx) => {
          const currentApp = pinnedApplications[slotIdx];
          const IconComponent = currentApp ? APP_ICON_MAP[currentApp.slug] || AppWindow : null;
          const brandColor = currentApp ? APP_COLOR_MAP[currentApp.slug] || '#3b82f6' : '#94a3b8';

          return (
            <div key={slotIdx} className="pinned-slot-row glass-panel" style={{ padding: '14px 18px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1 }}>
                <span className="slot-badge" style={{ fontWeight: '700', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  Slot #{slotIdx + 1}
                </span>

                {currentApp ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div className="slot-app-icon" style={{ background: `${brandColor}18`, color: brandColor, width: '32px', height: '32px', borderRadius: '8px', display: 'grid', placeItems: 'center' }}>
                      <IconComponent size={17} />
                    </div>
                    <strong>{currentApp.name}</strong>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>({currentApp.category})</span>
                  </div>
                ) : (
                  <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.85rem' }}>
                    Empty Slot
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <select
                  className="input-field"
                  style={{ width: '220px', padding: '8px 12px', fontSize: '0.84rem' }}
                  value={currentApp?.slug || ''}
                  onChange={(e) => handleSelectAppForPosition(slotIdx, e.target.value)}
                  disabled={saving}
                >
                  <option value="">-- None (Clear Slot) --</option>
                  {availableApps.map((a) => (
                    <option key={a.slug} value={a.slug}>
                      {a.name}
                    </option>
                  ))}
                </select>

                {currentApp && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      type="button"
                      className="btn-icon-subtle"
                      onClick={() => handleMove(slotIdx, -1)}
                      disabled={slotIdx === 0 || saving}
                      title="Move Up"
                    >
                      <ArrowUp size={15} />
                    </button>
                    <button
                      type="button"
                      className="btn-icon-subtle"
                      onClick={() => handleMove(slotIdx, 1)}
                      disabled={slotIdx >= pinnedApplications.length - 1 || saving}
                      title="Move Down"
                    >
                      <ArrowDown size={15} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
